import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { moleculeCardDepiction } from './moleculeCardDepiction';
import {
  aromaticBondIds,
  bondStereoValues,
  drawnBondOrders
} from '@peruselab/structure-editor/domain/documentSelectors';
import { drawingDocumentToSvg } from '@peruselab/structure-editor/rendering/svgExport';
import { croppedToContent } from '@peruselab/structure-editor/rendering/contentExtent';
import type { DrawingDocument } from '@peruselab/structure-editor/model';
import { fetchMolecule } from './moleculeCache';
import { SvgMarkup } from './SvgMarkup';

/** #927: atoms and bonds to mark (a search match), by the Molecule document's own node and edge ids. */
export interface StructureHighlight {
  readonly atomIds: readonly string[];
  readonly bondIds: readonly string[];
}

interface MoleculeThumbnailProps {
  readonly moleculeId: string;
  readonly size?: number;
  /** #927: marks a match beneath the structure. */
  readonly highlight?: StructureHighlight | undefined;
  /** #927: the picture's accessible name ("Structure of aspirin"). Without one the picture is
   *  decorative -- right inside a card that already names the molecule. */
  readonly label?: string | undefined;
  /** #1178: the piece of the paper this molecule is Identical to -- drawn instead of the record, so
   *  the card shows what the paper (and its exports) show. */
  readonly paperPiece?: DrawingDocument | undefined;
}

type ThumbnailState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly svg: string }
  | { readonly status: 'error'; readonly message: string };

const THUMBNAIL_COORDINATE_SIZE = { width: 240, height: 200 };
const THUMBNAIL_COLOR = '#1f2933';

// #191: a read-only 2D structure image for a bare Molecule id -- used by search-result cards,
// which only carry a moleculeId (no isomer / stereochemistry). Fetches the Molecule, lays it
// out via generateMoleculeDepiction (the molecule-only core of the isomer-depiction path), and
// renders through the same SVG pipeline the "Export SVG" button uses.
//
// One in-flight promise per moleculeId is memoised module-wide, so the same molecule appearing
// in several result groups fetches and depicts once. A rejected attempt is evicted so a later
// mount can retry.
const svgByMoleculeId = new Map<string, Promise<string>>();

const highlightKey = (highlight: StructureHighlight | undefined) =>
  highlight ? `|${[...highlight.atomIds].sort().join(',')}|${[...highlight.bondIds].sort().join(',')}` : '';

const svgByPaperPiece = new WeakMap<DrawingDocument, Promise<string>>();

/** #1178: a piece of the paper as a card's picture -- its chemistry as drawn, cut down to it. */
export function paperPieceSvg(piece: DrawingDocument): Promise<string> {
  const cached = svgByPaperPiece.get(piece);
  if (cached) return cached;
  const document = croppedToContent({ ...piece, annotations: [] });
  const svg = Promise.resolve(
    drawingDocumentToSvg(
      document,
      {
        bondOrders: drawnBondOrders(document),
        aromaticBondIds: aromaticBondIds(document),
        stereoBonds: bondStereoValues(document),
        overriddenBondIds: new Set()
      },
      THUMBNAIL_COLOR,
      { width: document.depiction.coordinateSet.width, height: document.depiction.coordinateSet.height },
      // #429: its own marker ids, apart from the paper's and every other picture's.
      `p-${document.structure.graph.id.replace(/[^A-Za-z0-9_-]/g, '')}-`,
      {},
      [],
      []
    )
  );
  svgByPaperPiece.set(piece, svg);
  return svg;
}

/** The picture's SVG at the standard bond length (its own width and height); #963 enlarges it. */
export function moleculeThumbnailSvg(moleculeId: string, highlight?: StructureHighlight): Promise<string> {
  const cacheKey = moleculeId + highlightKey(highlight);
  const cached = svgByMoleculeId.get(cacheKey);
  if (cached) return cached;
  const pending = (async () => {
    const molecule = await fetchMolecule(moleculeId);
    const document = await moleculeCardDepiction(moleculeId, molecule.document, THUMBNAIL_COORDINATE_SIZE);
    // #940: the depiction gives atoms and bonds new ids, in the same order -- translate the
    // highlight (given in the Molecule's own ids) by position.
    const byPosition = (from: readonly { id: string }[], to: readonly { id: string }[], ids: readonly string[]) =>
      ids.flatMap((id) => {
        const index = from.findIndex((item) => item.id === id);
        const target = index >= 0 ? to[index] : undefined;
        return target ? [target.id] : [];
      });
    const shownHighlight = highlight
      ? {
          atomIds: new Set(
            byPosition(molecule.document.graph.nodes, document.structure.graph.nodes, highlight.atomIds)
          ),
          bondIds: new Set(byPosition(molecule.document.graph.edges, document.structure.graph.edges, highlight.bondIds))
        }
      : null;
    return drawingDocumentToSvg(
      document,
      {
        bondOrders: drawnBondOrders(document),
        aromaticBondIds: aromaticBondIds(document),
        stereoBonds: bondStereoValues(document),
        overriddenBondIds: new Set()
      },
      THUMBNAIL_COLOR,
      // #518 -- the document's own fitted coordinate size, not THUMBNAIL_COORDINATE_SIZE. That
      // constant is only a *floor* for generateMoleculeDepiction (fittedCoordinates lays out
      // every molecule at the same fixed bond-length scale and only grows the canvas for
      // molecules that need more room -- see its own comment). Passing the fixed constant back in
      // here as the *export* size would re-normalize every molecule's already-relative
      // coordinates onto the same fixed canvas, discarding exactly the true-relative-size
      // information fittedCoordinates preserved -- a big molecule and a tiny one would always
      // come out the same rendered size. Using the document's actual coordinateSet dimensions
      // instead makes the exported SVG's own pixel size a real, comparable measurement (in the
      // same 30-units-per-bond scale) across every molecule, so a CSS `object-fit`-style
      // shrink-only rule (search-result-card's .isomer-thumbnail-svg override) can tell them apart.
      { width: document.depiction.coordinateSet.width, height: document.depiction.coordinateSet.height },
      // #429 -- namespace this inlined SVG's <marker> ids so they can't collide with the live
      // canvas's (which would blank a dative bond's arrowhead) or another thumbnail's.
      `m-${moleculeId.replace(/[^A-Za-z0-9_-]/g, '')}${highlight ? 'h' : ''}-`,
      {},
      [],
      [],
      shownHighlight ? { highlight: shownHighlight } : {}
    );
  })();
  pending.catch(() => svgByMoleculeId.delete(cacheKey));
  svgByMoleculeId.set(cacheKey, pending);
  return pending;
}

export function MoleculeThumbnail({ moleculeId, size = 96, highlight, label, paperPiece }: MoleculeThumbnailProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<ThumbnailState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    (paperPiece ? paperPieceSvg(paperPiece) : moleculeThumbnailSvg(moleculeId, highlight))
      .then((svg) => {
        if (!cancelled) setState({ status: 'ready', svg });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            status: 'error',
            message: error instanceof Error ? error.message : t('moleculeThumbnail.renderError')
          });
        }
      });
    return () => {
      cancelled = true;
    };
    // highlightKey: a new array with the same ids is the same picture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moleculeId, highlightKey(highlight), paperPiece, t]);

  return (
    <div
      className="isomer-thumbnail"
      style={{ width: size, height: size }}
      {...(label ? { role: 'img', 'aria-label': label } : {})}
    >
      {state.status === 'loading' ? <span className="isomer-thumbnail-status">{t('common.loading')}</span> : null}
      {state.status === 'error' ? (
        <span className="isomer-thumbnail-status isomer-thumbnail-error">{state.message}</span>
      ) : null}
      {state.status === 'ready' ? (
        // Generated entirely by drawingDocumentToSvg -- numeric coordinates and fixed element
        // symbols, no user-controlled text -- the same trust level as the SVG-export download.
        <SvgMarkup className="isomer-thumbnail-svg" svg={state.svg} />
      ) : null}
    </div>
  );
}
